import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Calendar, PlusCircle, ArrowUpRight, Activity, ClipboardList, AlertTriangle, Target, Clock } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { CONSULTATION_TYPES, STATUS_LABELS } from "@/lib/aga";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/contexts/AuthContext";

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));

// Return the doctor's title + first meaningful name (skip Dr. prefix if present)
function greeting(name) {
    if (!name) return "Doutor";
    const parts = name.split(/\s+/).filter(Boolean);
    // If first token is Dr. / Dra. keep the next token; otherwise show first name
    if (/^(dr\.?|dra\.?)$/i.test(parts[0])) return `${parts[0]} ${parts[1] || ""}`.trim();
    return parts[0];
}

export default function DoctorDashboard() {
    const [patients, setPatients] = useState([]);
    const [consultations, setConsultations] = useState([]);
    const [atRisk, setAtRisk] = useState([]);
    const [focusToday, setFocusToday] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        (async () => {
            try {
                // Use allSettled so a 403 on any single endpoint (e.g., nurse hitting
                // /monitoring/at-risk which is doctor/admin-only) doesn't crash the
                // whole dashboard via the global error overlay.
                const [p, c, r, f] = await Promise.allSettled([
                    api.get("/patients"),
                    api.get("/consultations"),
                    api.get("/monitoring/at-risk"),
                    api.get("/monitoring/focus-today"),
                ]);
                if (p.status === "fulfilled") setPatients(p.value.data);
                if (c.status === "fulfilled") setConsultations(c.value.data);
                if (r.status === "fulfilled") {
                    // /at-risk now returns { patients: [...], totals: {...} }
                    const payload = r.value.data;
                    setAtRisk(Array.isArray(payload) ? payload : (payload.patients || []));
                }
                if (f.status === "fulfilled") setFocusToday(f.value.data || []);
            } finally { setLoading(false); }
        })();
    }, []);

    const highRiskCount = atRisk.filter(r => r.risk_band === "critical" || r.risk_band === "high").length;

    const stats = [
        { icon: Users, label: "Pacientes", value: patients.length },
        { icon: Calendar, label: "Consultas", value: consultations.length },
        { icon: AlertTriangle, label: "Alto risco", value: highRiskCount, accent: highRiskCount > 0 },
        { icon: Activity, label: "Em andamento", value: consultations.filter(c => c.status === "in_progress" || c.status === "intake_completed").length },
    ];

    return (
        <AppShell>
            <div className="mb-8">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">
                    {user?.specialty || "Consultório clínico"}
                </div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Bem-vindo, {greeting(user?.name)}</h1>
                <p className="text-[var(--text-secondary)] mt-1">Painel de acompanhamento clínico e planos de cuidado.</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {stats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={i} className={`card-earthy ${s.accent ? "border-l-4 border-[var(--brand-accent)]" : ""}`}>
                            <div className="flex items-start justify-between">
                                <Icon size={20} className={s.accent ? "text-[var(--brand-accent)]" : "text-[var(--brand-secondary)]"} />
                                <span className="text-xs text-muted-clinical uppercase tracking-wider">{s.label}</span>
                            </div>
                            <div className={`font-display text-3xl font-semibold mt-3 ${s.accent ? "text-[var(--brand-accent)]" : "text-brand"}`}>{loading ? "…" : s.value}</div>
                        </div>
                    );
                })}
            </div>

            {highRiskCount > 0 && (
                <div className="card-earthy mb-6 bg-[#FBEEE6] border-l-4 border-[var(--brand-accent)]">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle size={18} className="text-[var(--brand-accent)]" />
                                <h2 className="font-display text-lg font-medium text-brand">Pacientes que precisam de atenção agora</h2>
                            </div>
                            <p className="text-sm text-[var(--text-secondary)]">
                                {highRiskCount} paciente{highRiskCount > 1 ? "s" : ""} com risco alto ou crítico de piora clínica.
                            </p>
                        </div>
                        <Link to="/monitoring" className="btn-primary text-sm shrink-0">Ver painel de risco</Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
                        {atRisk.filter(r => r.risk_band === "critical" || r.risk_band === "high").slice(0, 6).map((r) => (
                            <Link
                                key={r.patient_id}
                                to={`/monitoring/patient/${r.patient_id}`}
                                data-testid={`dash-at-risk-${r.patient_id}`}
                                className="block p-3 rounded-lg bg-white hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-brand truncate">{r.patient_name}</div>
                                        <div className="text-xs text-muted-clinical mt-0.5 truncate">{r.top_findings[0] || "—"}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-display text-lg font-semibold text-[var(--brand-accent)]">{r.risk_score}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {focusToday.length > 0 && (
                <div className="card-earthy mb-6 border-l-4 border-[#2E7D6B]" data-testid="focus-today-widget">
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Target size={18} className="text-[#2E7D6B]" />
                                <h2 className="font-display text-lg font-medium text-brand">Meu foco de hoje</h2>
                            </div>
                            <p className="text-sm text-[var(--text-secondary)]">
                                Top {focusToday.length} paciente{focusToday.length > 1 ? "s" : ""} priorizado{focusToday.length > 1 ? "s" : ""} por risco composto e tempo sem ação clínica.
                            </p>
                        </div>
                        <Link to="/monitoring" className="btn-ghost text-sm shrink-0">Ver todos</Link>
                    </div>
                    <div className="space-y-2">
                        {focusToday.map((r) => (
                            <Link
                                key={r.patient_id}
                                to={`/monitoring/patient/${r.patient_id}`}
                                data-testid={`focus-today-${r.patient_id}`}
                                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white hover:shadow-md transition-shadow"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-brand truncate">{r.patient_name}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-[#2E7D6B] font-semibold">{r.reason}</span>
                                    </div>
                                    {r.chips?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {r.chips.map((c, i) => (
                                                <span
                                                    key={i}
                                                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                                                        c.tone === "critical"
                                                            ? "bg-[#9E2A2B] text-white"
                                                            : "bg-[#D4A373] text-white"
                                                    }`}
                                                >
                                                    {c.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <Clock size={14} className="text-muted-clinical" />
                                    <span className="text-xs text-muted-clinical">
                                        {r.last_action_at
                                            ? new Date(r.last_action_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
                                            : "—"}
                                    </span>
                                    <div className="font-display text-xl font-semibold text-[var(--brand-accent)]">{r.risk_score}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-3 mb-6">
                <button data-testid={TID.doctor.newPatientBtn} onClick={() => navigate("/patients/new")} className="btn-primary">
                    <PlusCircle size={16} /> Novo paciente
                </button>
                <button data-testid={TID.doctor.newConsultationBtn} onClick={() => navigate("/consultations/new")} className="btn-secondary">
                    <PlusCircle size={16} /> Nova consulta
                </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="card-earthy">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-xl font-medium text-brand">Consultas recentes</h2>
                    </div>
                    {consultations.length === 0 ? (
                        <p className="text-sm text-muted-clinical">Nenhuma consulta ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {consultations.slice(0, 8).map((c) => (
                                <Link
                                    to={`/consultations/${c.id}`}
                                    key={c.id}
                                    data-testid={TID.doctor.consultationRow(c.id)}
                                    className="block p-3 rounded-lg hover:bg-alt transition-colors border border-transparent hover:border-[rgba(30,63,51,0.1)]"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-brand">{c.patient_name}</div>
                                            <div className="text-xs text-muted-clinical mt-0.5">{typeLabel[c.consultation_type]}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs font-semibold px-2 py-0.5 rounded-full bg-alt text-brand-secondary inline-flex">
                                                {STATUS_LABELS[c.status]}
                                            </div>
                                            <ArrowUpRight size={14} className="inline-block ml-2 text-muted-clinical" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                <div className="card-earthy">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display text-xl font-medium text-brand">Pacientes</h2>
                    </div>
                    {patients.length === 0 ? (
                        <p className="text-sm text-muted-clinical">Cadastre seu primeiro paciente.</p>
                    ) : (
                        <div className="space-y-2">
                            {patients.slice(0, 8).map((p) => (
                                <Link
                                    to={`/patients/${p.id}`}
                                    key={p.id}
                                    data-testid={TID.doctor.patientRow(p.id)}
                                    className="block p-3 rounded-lg hover:bg-alt transition-colors border border-transparent hover:border-[rgba(30,63,51,0.1)]"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-brand">{p.full_name}</div>
                                            <div className="text-xs text-muted-clinical mt-0.5">{p.insurance || "Sem convênio informado"} · {p.city_uf || "—"}</div>
                                        </div>
                                        <ArrowUpRight size={14} className="text-muted-clinical" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AppShell>
    );
}
